/** Utility functions for task data operations, separated from React hook logic. */
// @ts-check
import { ensureTermsExist } from '@/services/tagService';
import { buildNameMapForTerms } from '@/services/tagNameUtils';
import { updateTaskSchema } from '@/features/tasks/utils/data/taskSchema';
import {
  updateTask,
  updateTaskDuration as updateTaskDurationService,
} from '@/services/taskMutations';

/**
 * @param {string} taskId
 * @param {string} userId
 * @returns {boolean}
 */
export const validateUpdateInputs = (taskId, userId) => {
  if (typeof taskId !== 'string' || taskId.length === 0) {
    console.error(
      'Invalid taskId for updateTaskDetails: must be a non-empty string.',
    );
    return false;
  }
  if (typeof userId !== 'string' || userId.length === 0) {
    console.error(
      'Invalid userId for updateTaskDetails: must be a non-empty string.',
    );
    return false;
  }
  return true;
};

/**
 * @param {any} taskData
 * @returns {any}
 */
export const parseUpdateFields = (taskData) => {
  const updatedFields = { ...taskData };

  // Parse complex title structure
  if (typeof taskData.title === 'object' && taskData.title !== null) {
    const { title, projects, contexts } = taskData.title;
    updatedFields.title = title;
    updatedFields.projects = projects || [];
    updatedFields.contexts = contexts || [];
  }

  // Handle duration parsing
  if (taskData.duration !== undefined) {
    updatedFields.duration = parseInt(taskData.duration, 10) || 0;
  }

  return updatedFields;
};

/**
 * @param {string} taskId
 * @param {any} taskData
 * @param {string} userId
 */
export const executeTaskUpdateDetails = async (taskId, taskData, userId) => {
  if (!validateUpdateInputs(taskId, userId)) return;

  try {
    const updatedFields = parseUpdateFields(taskData);

    // Ensure related terms exist in DB
    if (updatedFields.projects) {
      await ensureTermsExist(
        updatedFields.projects,
        'projects',
        userId,
        buildNameMapForTerms(
          updatedFields.projects,
          updatedFields.projectNames,
        ),
      );
    }
    if (updatedFields.contexts) {
      await ensureTermsExist(
        updatedFields.contexts,
        'contexts',
        userId,
        buildNameMapForTerms(
          updatedFields.contexts,
          updatedFields.contextNames,
        ),
      );
    }

    const validationResult = updateTaskSchema.safeParse(updatedFields);
    if (!validationResult.success) {
      console.error(
        'Validation failed for task update:',
        validationResult.error.flatten(),
      );
      return;
    }

    if (Object.keys(validationResult.data).length > 0) {
      await updateTask(taskId, validationResult.data);
    }
  } catch (error) {
    console.error('Error while updating task details:', error);
    throw error;
  }
};

/**
 * @param {string} taskId
 * @param {number} newDuration
 */
export const executeUpdateTaskDuration = async (taskId, newDuration) => {
  if (!taskId) {
    console.error('No task ID provided to updateTaskDuration');
    return;
  }
  try {
    await updateTaskDurationService(taskId, newDuration);
  } catch (error) {
    console.error('Error while updating task duration:', error);
  }
};
